import React, { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import cn from 'classnames'
import useStore from 'state/knovStore'
import { TOP_LAYER_CHANGE_EVENT, topmostModalDialog } from 'lib/topLayer'
import styles from './global-notice.module.scss'

export default function GlobalNotice() {
    const notice = useStore(state => state.globalNotice)
    const clearGlobalNotice = useStore(state => state.clearGlobalNotice)

    const isPending = notice?.variant === 'pending'

    // While a modal <dialog> (the HTML ordinal fullscreen viewer) owns the screen, everything
    // outside it is inert and beneath it — the top layer ignores z-index. The notice therefore
    // renders INSIDE that dialog for as long as it is open, and re-homes when one opens or closes.
    const [topLayerVersion, setTopLayerVersion] = useState(0)
    useEffect(() => {
        const rehome = () => setTopLayerVersion(version => version + 1)
        window.addEventListener(TOP_LAYER_CHANGE_EVENT, rehome)
        return () => window.removeEventListener(TOP_LAYER_CHANGE_EVENT, rehome)
    }, [])

    useEffect(() => {
        if (!notice) return
        // Pending notices (in-flight status) stay up until updated or cleared explicitly.
        if (isPending || !Number.isFinite(notice.durationMs) || notice.durationMs <= 0) return

        const timeout = window.setTimeout(() => {
            clearGlobalNotice(notice.id)
        }, notice.durationMs)

        return () => window.clearTimeout(timeout)
    }, [clearGlobalNotice, notice, isPending])

    if (!notice) return null

    const hasActions = !!(notice.actionLabel || notice.cancelLabel || notice.onAction)
    const handleAction = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation()
        notice.onAction?.()
        clearGlobalNotice(notice.id)
    }
    const handleCancel = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation()
        clearGlobalNotice(notice.id)
    }

    const noticeElement = (
        <div className={styles.noticeLayer} aria-live="assertive" aria-atomic="true">
            <div
                className={cn(styles.noticePill, styles[notice.variant])}
                data-kind={notice.kind}
                data-testid={notice.testId}
                onClick={() => {
                    // Don't let a stray click dismiss an in-flight status toast.
                    if (!hasActions && !isPending) clearGlobalNotice(notice.id)
                }}
                role={hasActions ? 'alertdialog' : 'button'}
            >
                {isPending && <span className={styles.noticeSpinner} aria-hidden="true" />}
                <span className={styles.noticeMessage}>{notice.message}</span>
                {notice.detail && <span className={styles.noticeDetail}>{notice.detail}</span>}
                {hasActions && (
                    <span className={styles.noticeActions}>
                        <button
                            className={styles.noticeCancelButton}
                            onClick={handleCancel}
                            type="button"
                        >
                            {notice.cancelLabel || 'Cancel'}
                        </button>
                        {notice.onAction && (
                            <button
                                className={styles.noticeActionButton}
                                onClick={handleAction}
                                type="button"
                            >
                                {notice.actionLabel || 'Confirm'}
                            </button>
                        )}
                    </span>
                )}
            </div>
        </div>
    )

    if (typeof document === 'undefined') return noticeElement

    // `topLayerVersion` only exists to run this lookup again after a dialog opened or closed.
    void topLayerVersion
    return createPortal(noticeElement, topmostModalDialog() || document.body)
}
